import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="calibrate-curricular-action-items-card"
export default class extends Controller {
  static targets = [
    "checkbox",
    "createCourseFromSelectedButton",
    "exportButton",
    "deliveryCheckbox",
    "csrfTokenField",
    "itemsField",
    "deliveryIdsField",
  ];

  connect() {
    this._boundUpdateButtonState = this.updateButtonState.bind(this);
    this._boundCheckboxKeydown = this.handleCheckboxKeydown.bind(this);
    this._addEventListeners = this.addEventListeners.bind(this);
    this._removeEventListeners = this.removeEventListeners.bind(this);

    // Buttons & Checkboxes will appear on `calibrate-results-updated` event
    document.addEventListener(
      "calibrate-results-updated",
      this._addEventListeners
    );
  }

  addEventListeners() {
    this._boundUpdateButtonState();
    [...this.checkboxTargets, ...this.deliveryCheckboxTargets].forEach((cb) => {
      cb.addEventListener("change", this._boundUpdateButtonState);
      cb.addEventListener("keydown", this._boundCheckboxKeydown);
    });
  }

  removeEventListeners() {
    this.updateButtonState();
    [...this.checkboxTargets, ...this.deliveryCheckboxTargets].forEach((cb) => {
      cb.removeEventListener("change", this._boundUpdateButtonState);
      cb.removeEventListener("keydown", this._boundCheckboxKeydown);
    });
  }

  disconnect() {
    if (this._removeEventListeners) {
      document.removeEventListener(
        "calibrate-results-updated",
        this._removeEventListeners
      );
    }
  }

  handleCheckboxKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.target.checked = !event.target.checked;
      // Fire change event so listeners update
      event.target.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  updateButtonState() {
    const anyChecked = this.checkboxTargets.some((cb) => cb.checked);
    if (this.hasCreateCourseFromSelectedButtonTarget) {
      this.createCourseFromSelectedButtonTarget.disabled = !anyChecked;
    }
    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.disabled = !anyChecked;
    }
  }

  createCourseFromSelected(event) {
    event.preventDefault();
    const selectedIds = Array.from(
      new Set(
        this.checkboxTargets
          .filter((cb) => cb.checked)
          .map((cb) => cb.dataset.documentId)
      )
    );

    if (selectedIds.length === 0) {
      alert("Please select at least one item.");
      return;
    }

    fetch("/educator_dashboard/create_custom_course_from_selected", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getMetaValue("csrf-token"),
      },
      body: JSON.stringify({ item_ids: selectedIds }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.showConfirmationAndRedirect(
            data.document_set_id,
            data.document_set_name
          );
        } else {
          alert(data.error || "An error occurred.");
        }
      })
      .catch(() => alert("An error occurred."));
  }

  setExportFields() {
    const csrfToken = this.getMetaValue("csrf-token");

    if (this.hasCsrfTokenFieldTarget && csrfToken) {
      this.csrfTokenFieldTarget.value = csrfToken;
    }

    const selectedItems = this.checkboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => ({
        id: cb.dataset.documentId,
        dp: cb.dataset.deliveryPurpose,
        di: cb.dataset.deliveryIds,
        dt: cb.dataset.documentTitle,
        st: cb.dataset.studentsAnsweredCount,
        lo: cb.dataset.learningObjective,
        tp: cb.dataset.teachingPoint,
        sy: cb.dataset.system,
        cf: cb.dataset.clinicalFocus,
        ef: cb.dataset.excelFraction,
        co: cb.dataset.competentFraction,
        un: cb.dataset.unsureFraction,
        no: cb.dataset.noviceFraction,
        wf: cb.dataset.warningFraction,
        cc: cb.dataset.caseCompletedFraction,
      }));

    const selectedDeliveryIds = this.deliveryCheckboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => cb.dataset.deliveryId);

    if (this.hasItemsFieldTarget) {
      this.itemsFieldTarget.value = JSON.stringify(selectedItems);
    }

    if (this.hasDeliveryIdsFieldTarget) {
      this.deliveryIdsFieldTarget.value = JSON.stringify(selectedDeliveryIds);
    }
  }

  showConfirmation(message, { redirectUrl = null } = {}) {
    let confirmationDiv = document.getElementById("confirmation-popup");
    if (!confirmationDiv) {
      confirmationDiv = document.createElement("div");
      confirmationDiv.id = "confirmation-popup";
      confirmationDiv.style.position = "fixed";
      confirmationDiv.style.top = "20px";
      confirmationDiv.style.left = "50%";
      confirmationDiv.style.transform = "translateX(-50%)";
      confirmationDiv.style.background = "#4caf50";
      confirmationDiv.style.color = "white";
      confirmationDiv.style.padding = "12px 24px";
      confirmationDiv.style.borderRadius = "6px";
      confirmationDiv.style.zIndex = 9999;
      confirmationDiv.style.fontSize = "1.1em";
      document.body.appendChild(confirmationDiv);
    }
    confirmationDiv.textContent = message;
    confirmationDiv.style.display = "block";
    setTimeout(() => {
      confirmationDiv.style.display = "none";
      if (redirectUrl) {
        window.open(redirectUrl, "_blank");
      }
    }, 1500);
  }

  showConfirmationAndRedirect(documentSetId, documentSetName) {
    this.showConfirmation(`Course created: ${documentSetName}`, {
      redirectUrl: `/document_sets/${documentSetId}/edit`,
    });
  }

  getMetaValue(name) {
    const element = document.head.querySelector(`meta[name=\"${name}\"]`);
    return element && element.content;
  }
}
